import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowDown, ArrowUp, EyeOff, Home, Lock, Menu, Plus } from "lucide-react";
import { toast } from "sonner";
import { adminApi, bestSrc, errText } from "@/lib/api";
import { AdminLayout } from "@/pages/admin/AdminLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";

const slugify = (s) => s.toLowerCase().trim().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "");

const NewPage = () => {
  const nav = useNavigate();
  const [open, setOpen] = useState(false);
  const [title, setTitle] = useState("");
  const create = async (e) => {
    e.preventDefault();
    try {
      const { data } = await adminApi.post("/admin/pages", { title, slug: slugify(title), menu_label: title, hidden: true, in_menu: false });
      nav(`/admin/pages/${data.id}`);
    } catch (ex) {
      toast.error(errText(ex));
    }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button className="rounded-full" data-testid="new-page-button"><Plus size={16} className="mr-1" /> New page</Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader><DialogTitle>New page</DialogTitle></DialogHeader>
        <form onSubmit={create} className="space-y-3">
          <Input placeholder="Page title, e.g. Resources" value={title} onChange={(e) => setTitle(e.target.value)} required data-testid="new-page-title-input" />
          <p className="text-xs text-neutral-500">Address: /{slugify(title) || "…"} — starts hidden; you can add it to the menu later.</p>
          <Button type="submit" className="w-full rounded-full" data-testid="create-page-submit">Create</Button>
        </form>
      </DialogContent>
    </Dialog>
  );
};

const Row = ({ p, i, last, onMove }) => (
  <div className="flex items-center gap-3 sm:gap-4 bg-white rounded-2xl p-3 pr-2 sm:pr-4 min-w-0" data-testid={`admin-page-row-${p.slug}`}>
    <Link to={`/admin/pages/${p.id}`} className="shrink-0 w-20 h-20 rounded-xl overflow-hidden bg-neutral-100">
      {p.cover && <img src={bestSrc(p.cover, 300)} alt="" className="w-full h-full object-cover" loading="lazy" />}
    </Link>
    <Link to={`/admin/pages/${p.id}`} className="flex-1 min-w-0" data-testid={`admin-page-open-${p.slug}`}>
      <div className="font-semibold truncate">{p.title}</div>
      <div className="text-xs text-neutral-500 truncate">/{p.slug} · {p.image_count} photos</div>
      <div className="flex flex-wrap gap-1.5 mt-2">
        {p.in_menu && !p.hidden && <span className="text-[11px] bg-[#61ce70]/20 text-green-900 rounded-full px-2 py-0.5 flex items-center gap-1"><Menu size={11} /> In menu</span>}
        {p.hidden && <span className="text-[11px] bg-neutral-900 text-white rounded-full px-2 py-0.5 flex items-center gap-1"><EyeOff size={11} /> Hidden</span>}
        {p.has_password && <span className="text-[11px] bg-amber-100 text-amber-900 rounded-full px-2 py-0.5 flex items-center gap-1"><Lock size={11} /> Password</span>}
      </div>
    </Link>
    <div className="flex flex-col gap-1">
      <Button variant="ghost" size="icon" disabled={i === 0} onClick={() => onMove(i, -1)} data-testid={`admin-page-up-${p.slug}`}><ArrowUp size={16} /></Button>
      <Button variant="ghost" size="icon" disabled={last} onClick={() => onMove(i, 1)} data-testid={`admin-page-down-${p.slug}`}><ArrowDown size={16} /></Button>
    </div>
  </div>
);

export default function AdminDashboard() {
  const [pages, setPages] = useState(null);
  const [status, setStatus] = useState(null);

  const load = () => {
    adminApi.get("/admin/pages").then((r) => setPages(Array.isArray(r.data) ? r.data : []));
    adminApi.get("/admin/status").then((r) => setStatus(r.data)).catch(() => {});
  };
  useEffect(() => {
    load();
  }, []);

  const move = async (i, d) => {
    const next = [...pages];
    const [m] = next.splice(i, 1);
    next.splice(i + d, 0, m);
    setPages(next);
    try {
      await adminApi.put("/admin/pages-order", { ids: next.map((p) => p.id) });
    } catch (ex) {
      toast.error(errText(ex));
    }
  };

  const visible = (pages || []).filter((p) => !p.hidden);
  const hidden = (pages || []).filter((p) => p.hidden);

  return (
    <AdminLayout title="Your site" actions={<NewPage />}>
      {status && !status.import_done && (
        <div className="mb-4 rounded-xl bg-amber-50 text-amber-900 text-sm p-3" data-testid="import-status">Copying your photos from the old site… ({status.media} so far). Refresh in a minute.</div>
      )}
      <Link to="/admin/home" className="flex items-center gap-4 bg-black text-white rounded-2xl p-5 mb-8 hover:bg-neutral-800 transition-colors" data-testid="admin-home-images-link">
        <Home size={22} />
        <div>
          <div className="font-semibold">Home page photos</div>
          <div className="text-sm text-neutral-400">Choose the 5 strips on the front page</div>
        </div>
      </Link>
      <h2 className="text-sm uppercase tracking-wider text-neutral-500 mb-3">Menu pages (in menu order)</h2>
      <div className="grid grid-cols-1 gap-2 mb-10" data-testid="admin-visible-pages">
        {pages === null && <p className="text-sm text-neutral-500">Loading…</p>}
        {visible.map((p) => {
          const i = pages.indexOf(p);
          return <Row key={p.id} p={p} i={i} last={i === pages.length - 1} onMove={move} />;
        })}
      </div>
      <h2 className="text-sm uppercase tracking-wider text-neutral-500 mb-3">Hidden pages (only people with the link)</h2>
      <div className="grid grid-cols-1 gap-2" data-testid="admin-hidden-pages">
        {pages !== null && !hidden.length && <p className="text-sm text-neutral-500">No hidden pages yet.</p>}
        {hidden.map((p) => {
          const i = pages.indexOf(p);
          return <Row key={p.id} p={p} i={i} last={i === pages.length - 1} onMove={move} />;
        })}
      </div>
    </AdminLayout>
  );
}
